import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, buildUrl } from "@shared/routes";
import { InsertProfile } from "@shared/schema";
import type { Profile, TechnicianSchedule, InsertTechnicianSchedule } from "@shared/schema";
import { authFetch } from "@/lib/authFetch";
import { supabase } from "@/lib/supabase";

/** Injecte les comptes super_admin actifs dans une liste filtrée s'ils n'y figurent pas déjà. */
export function withSuperAdmin(filtered: Profile[], allProfiles: Profile[]): Profile[] {
  const superAdmins = allProfiles.filter(
    p => p.isActive && (p as any).accessRole === "super_admin",
  );
  if (superAdmins.length === 0) return filtered;
  const result = [...filtered];
  for (const sa of superAdmins) {
    if (!result.some(p => p.id === sa.id)) {
      result.push({ ...sa, lastName: `${sa.lastName} (super admin)` } as Profile);
    }
  }
  return result;
}

export type DocumentInfo = {
  name: string;
  size: number;
  createdAt: string;
  url: string;
};

export function useProfiles() {
  return useQuery<Profile[]>({
    queryKey: [api.profiles.list.path],
    queryFn: async () => {
      const res = await authFetch(api.profiles.list.path);
      if (!res.ok) throw new Error("Failed to fetch profiles");
      return res.json();
    },
  });
}

export function useTechnicians() {
  return useQuery<Profile[]>({
    queryKey: [api.profiles.list.path, "technicians"],
    queryFn: async () => {
      const res = await authFetch(api.profiles.list.path);
      if (!res.ok) throw new Error("Failed to fetch profiles");
      const profiles: Profile[] = await res.json();
      const filtered = profiles.filter(p => p.role === "technicien" && p.isActive);
      return withSuperAdmin(filtered, profiles);
    },
  });
}

export function useProfile(id: string) {
  return useQuery({
    queryKey: [api.profiles.get.path, id],
    queryFn: async () => {
      const url = buildUrl(api.profiles.get.path, { id });
      const res = await authFetch(url);
      if (!res.ok) throw new Error("Failed to fetch profile");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; rolesSecondaires?: string[] } & Partial<InsertProfile>) => {
      const url = buildUrl(api.profiles.update.path, { id });
      const res = await authFetch(url, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update profile");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.profiles.list.path] }),
  });
}

export function useDeleteProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/profiles/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Erreur suppression");
      }
      return res.json() as Promise<{ softDeleted: boolean; linkedItems: string[] }>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.profiles.list.path] }),
  });
}

export function useTechnicianSchedules() {
  return useQuery<TechnicianSchedule[]>({
    queryKey: [api.technicianSchedules.list.path],
    queryFn: async () => {
      const res = await authFetch(api.technicianSchedules.list.path);
      if (!res.ok) throw new Error("Failed to fetch schedules");
      return res.json();
    },
  });
}

export function useCreateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertTechnicianSchedule) => {
      const res = await authFetch(api.technicianSchedules.create.path, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create schedule");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.technicianSchedules.list.path] }),
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<InsertTechnicianSchedule>) => {
      const url = buildUrl(api.technicianSchedules.update.path, { id });
      const res = await authFetch(url, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update schedule");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.technicianSchedules.list.path] }),
  });
}

export function useUpsertScheduleDay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      technician_id: string;
      week_start: string;
      day: "monday" | "tuesday" | "wednesday" | "thursday" | "friday";
      status: string;
    }) => {
      const res = await authFetch("/api/schedules/upsert-day", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur lors de la mise à jour du statut");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.technicianSchedules.list.path] }),
  });
}

export function useBatchCreateSchedules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      technicianId: string;
      weeks: Array<{
        weekStart: string;
        mondayStatus: string;
        tuesdayStatus: string;
        wednesdayStatus: string;
        thursdayStatus: string;
        fridayStatus: string;
      }>;
    }) => {
      const res = await authFetch('/api/schedules/batch', {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Batch schedule creation failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.technicianSchedules.list.path] });
    },
  });
}

export function useCommercials() {
  return useQuery<Profile[]>({
    queryKey: [api.profiles.list.path, "commercials"],
    queryFn: async () => {
      const res = await authFetch(api.profiles.list.path);
      if (!res.ok) throw new Error("Failed to fetch profiles");
      const profiles: Profile[] = await res.json();
      const filtered = profiles.filter(p =>
        p.isActive &&
        (p.role === "commercial" || ((p as any).rolesSecondaires || []).includes("commercial"))
      );
      return withSuperAdmin(filtered, profiles);
    },
  });
}

export function useCreateCommercial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: string;
      hasDrivingLicense: boolean;
      agenceId?: string | null;
      commercialStatus: string;
    }) => {
      const res = await authFetch('/api/commercials', {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        const e: any = new Error(err.error || err.message || "Erreur création commercial");
        e.status = res.status;
        e.body = err;
        throw e;
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.profiles.list.path] });
    },
  });
}

export function useCreateTechnician() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: string;
      hasDrivingLicense: boolean;
      agenceId?: string | null;
    }) => {
      const res = await authFetch('/api/technicians', {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        const e: any = new Error(err.error || err.message || "Erreur création technicien");
        e.status = res.status;
        e.body = err;
        throw e;
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.profiles.list.path] });
    },
  });
}

export function useAdministration() {
  return useQuery<Profile[]>({
    queryKey: [api.profiles.list.path, "administration"],
    queryFn: async () => {
      const res = await authFetch(api.profiles.list.path);
      if (!res.ok) throw new Error("Failed to fetch profiles");
      const profiles: Profile[] = await res.json();
      return profiles.filter(p => p.role === "administration" && p.isActive);
    },
  });
}

export function useCreateAdminMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: string;
      adminRole?: string;
      agenceId?: string | null;
    }) => {
      const res = await authFetch('/api/administration', {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        const e: any = new Error(err.error || err.message || "Erreur création membre admin");
        e.status = res.status;
        e.body = err;
        throw e;
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.profiles.list.path] });
    },
  });
}

export function useDocuments(profileId: string | null) {
  return useQuery<DocumentInfo[]>({
    queryKey: ['/api/profiles', profileId, 'documents'],
    queryFn: async () => {
      const res = await authFetch(`/api/profiles/${profileId}/documents`);
      if (!res.ok) throw new Error("Failed to fetch documents");
      return res.json();
    },
    enabled: !!profileId,
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ profileId, file }: { profileId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const res = await fetch(`/api/profiles/${profileId}/documents`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      return res.json();
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['/api/profiles', vars.profileId, 'documents'] });
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ profileId, filename }: { profileId: string; filename: string }) => {
      const res = await authFetch(`/api/profiles/${profileId}/documents/${encodeURIComponent(filename)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Delete failed");
      return res.json();
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['/api/profiles', vars.profileId, 'documents'] });
    },
  });
}

export function useUploadAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ profileId, file }: { profileId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const res = await fetch(`/api/profiles/${profileId}/avatar`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) throw new Error("Avatar upload failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.profiles.list.path] });
    },
  });
}
